import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { IntroductionComponent } from '../components/introduction/introduction.component';
import { PopupMessageComponent } from '../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../components/spinner/spinner.component';
import { EopAssistantComponent } from '../components/eop-assistant/eop-assistant.component';
import { PrvNextButtonsComponent } from '../components/prv-next-buttons/prv-next-buttons.component';
import { Icons } from '../../shared/helpers/icons';
import { IUserInfo } from '../../shared/models/contact';
import { ESectionTypes } from '../../shared/models/eop_types.enum';
import { UserDataService } from '../../shared/services/userData.service';
import { EopDocumentService } from '../../shared/services/eopDocument.service';
import { environment } from '../../environments/environment';
import { IEopDocument } from '../../shared/models/eopDocument';

interface IAreaField {
  key: string;
  label: string;
}

interface IAreaPageConfig {
  sectionType: ESectionTypes;
  title: string;
  /** Prefixed with the area number to label each input, e.g. "Evacuation Area 1". */
  fieldLabel: string;
  helpText: string;
}

type IAreasDoc = IEopDocument & { [key: string]: any };

// Both pages store the same four slots in the document's `description` column as JSON.
const AREA_FIELD_KEYS = ['area_1', 'area_2', 'area_3', 'area_4'];

const ROUTE_TO_AREA_PAGE: Record<string, IAreaPageConfig> = {
  'evacuation-areas': {
    sectionType: ESectionTypes['Evacuation Areas'],
    title: 'Evacuation Areas',
    fieldLabel: 'Evacuation Area',
    helpText: 'Identify up to four evacuation areas for the building.'
  },
  'reunification-areas': {
    sectionType: ESectionTypes['Reunification Areas'],
    title: 'Reunification Areas',
    fieldLabel: 'Reunification Area',
    helpText: 'Identify up to four reunification areas where students are released to parents or guardians.'
  }
};

@Component({
  selector: 'evacuation-areas',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    IntroductionComponent,
    PopupMessageComponent,
    SpinnerComponent,
    EopAssistantComponent,
    PrvNextButtonsComponent
  ],
  templateUrl: './evacuation-areas.component.html',
  styleUrl: './evacuation-areas.component.css'
})
export class EvacuationAreasComponent implements OnInit {

  icons = Icons;
  displayTitle: string = 'Evacuation Areas';
  eopSectionTypeId: number = ESectionTypes['Evacuation Areas'];
  helpText: string = ROUTE_TO_AREA_PAGE['evacuation-areas'].helpText;
  mapUrl: string = `${environment.nSidePlatformUrl.replace(/\/+$/, '')}/Maps.aspx`;

  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;

  areaFields: IAreaField[] = [];
  areasDoc: IAreasDoc = {};

  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string | null = null;

  constructor(
    private router: Router,
    private userDataService: UserDataService,
    private eopDocumentService: EopDocumentService
  ) {}

  ngOnInit(): void {
    this.applyRouteContext();
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadEopDoc();
  }

  private applyRouteContext(): void {
    const url = this.router.url.replace(/\/+$/, '');
    const segments = url.split('/');
    const lastSegment = segments[segments.length - 1] ?? '';

    const config = ROUTE_TO_AREA_PAGE[lastSegment] ?? ROUTE_TO_AREA_PAGE['evacuation-areas'];
    this.displayTitle = config.title;
    this.eopSectionTypeId = config.sectionType;
    this.helpText = config.helpText;
    this.areaFields = AREA_FIELD_KEYS.map((key, index) => ({
      key,
      label: `${config.fieldLabel} ${index + 1}`
    }));
  }

  loadEopDoc(): void {
    if (!this.currentBuildingId) return;

    this.loading = true;
    this.eopDocumentService.getEopDocument(this.currentBuildingId, this.eopSectionTypeId).subscribe({
      next: (response: any) => {
        this.loading = false;
        if (!response.has_error) {
          const doc: IEopDocument = response.data ?? {};
          this.areasDoc = { ...doc, ...this.parseDescription(doc.description) };
        }
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error loading evacuation areas document:', error.message);
      }
    });
  }

  onSubmit(): void {
    if (!this.currentBuildingId) return;

    this.loading = true;
    this.eopDocumentService.updateEopDocument(
      this.currentBuildingId,
      { description: this.serializeAreas() },
      this.eopSectionTypeId
    ).subscribe({
      next: (response) => {
        this.loading = false;
        if (!response.has_error) {
          this.messageModal = 'success';
          this.popupMessage = `This occurred after updating ${this.displayTitle}.`;
          this.loadEopDoc();
        } else {
          this.messageModal = 'unsuccess';
          this.popupMessage = response.message;
        }
      },
      error: (error) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  handleApproval(): void {
    if (!this.currentBuildingId) return;

    this.loading = true;
    this.eopDocumentService.approveEopDocument(this.currentBuildingId, this.eopSectionTypeId).subscribe({
      next: (response) => {
        this.loading = false;
        if (!response.has_error) {
          this.messageModal = 'success';
          this.popupMessage = `This occurred after confirming ${this.displayTitle}.`;
          this.loadEopDoc();
        } else {
          this.messageModal = 'unsuccess';
          this.popupMessage = response.message ?? 'An error occurred while confirming the document.';
        }
      },
      error: (error) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }

  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  isFormValid(): boolean {
    return this.areaFields.some(field => {
      const value = this.areasDoc[field.key];
      return value !== null && value !== undefined && String(value).trim() !== '';
    });
  }

  // Pages that used to be a single free-text box store plain text here rather than the
  // JSON area map, so keep that text in the first slot instead of dropping it on load.
  private parseDescription(description?: string): Record<string, any> {
    if (!description) return {};
    try {
      const parsed = JSON.parse(description);
      if (typeof parsed === 'object' && parsed !== null) return parsed;
    } catch {
      // Not JSON yet - fall through to the free-text migration below.
    }
    return description.trim() ? { [AREA_FIELD_KEYS[0]]: description.trim() } : {};
  }

  private serializeAreas(): string {
    const payload: Record<string, string> = {};
    this.areaFields.forEach(field => {
      const value = this.areasDoc[field.key];
      payload[field.key] = value === null || value === undefined ? '' : String(value);
    });
    return JSON.stringify(payload);
  }
}
